import { BlockStack, Card, InlineStack, ProgressBar, Text } from '@shopify/polaris'
import type { BccData } from './useBccData'
import type { ScorecardKpi } from '../../sdk/exec.api'
import { CardHeader } from './LensBadge'

// Which KPIs belong to each department
const DEPARTMENTS: {
  name:    string
  kpiIds:  string[]
}[] = [
  { name: 'Marketing',     kpiIds: ['fc.net_revenue', 'fc.avg_order_value'] },
  { name: 'Confirmation',  kpiIds: ['cf.confirmation_rate', 'sc.shipment_rate'] },
  { name: 'Warehouse',     kpiIds: ['sc.shipment_rate'] },
  { name: 'Shipping',      kpiIds: ['fc.delivery_rate', 'fc.return_rate'] },
  { name: 'Customer Svc',  kpiIds: ['cs.resolution_rate'] },
]

const KPI_SHORT: Record<string, string> = {
  'fc.net_revenue':       'Revenue',
  'fc.avg_order_value':   'AOV',
  'cf.confirmation_rate': 'Confirm rate',
  'sc.shipment_rate':     'Shipment rate',
  'fc.delivery_rate':     'Delivery rate',
  'fc.return_rate':       'Return rate',
  'cs.resolution_rate':   'Resolution',
}

type StatusTone = 'success' | 'critical' | 'caution' | undefined

function statusTone(kpi: ScorecardKpi): StatusTone {
  if (kpi.status === 'on_track')  return 'success'
  if (kpi.status === 'off_track') return 'critical'
  if (kpi.status === 'at_risk')   return 'caution'
  return undefined
}

function progressTone(kpi: ScorecardKpi): 'success' | 'critical' {
  return (kpi.achievementPct ?? 0) >= 85 ? 'success' : 'critical'
}

type Props = { data: BccData }

export function DepartmentScoreboard({ data }: Props) {
  const kpiMap = new Map<string, ScorecardKpi>(
    data.exec.kpiRows.map(k => [k.id, k])
  )

  return (
    <Card>
      <BlockStack gap="400">
        <CardHeader title="Department Scoreboard" lens="performance" maturity={data.dashboard.maturity} />

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 12,
        }}>
          {DEPARTMENTS.map(dept => {
            const kpis = dept.kpiIds
              .map(id => kpiMap.get(id))
              .filter(Boolean) as ScorecardKpi[]

            if (kpis.length === 0) return null

            // Dept-level status: worst KPI wins
            const worstStatus = kpis.some(k => k.status === 'off_track') ? 'off_track'
              : kpis.some(k => k.status === 'at_risk')                   ? 'at_risk'
              : 'on_track'

            const dotColor =
              worstStatus === 'off_track' ? 'var(--p-color-bg-fill-critical)' :
              worstStatus === 'at_risk'   ? 'var(--p-color-bg-fill-caution)'  :
                                            'var(--p-color-bg-fill-success)'

            return (
              <div
                key={dept.name}
                style={{
                  background: 'var(--p-color-bg-surface-secondary)',
                  border: '1px solid var(--p-color-border)',
                  borderRadius: 8,
                  padding: 12,
                }}
              >
                <BlockStack gap="300">
                  <InlineStack align="space-between" blockAlign="center">
                    <Text as="p" variant="bodySm" fontWeight="semibold">{dept.name}</Text>
                    <div style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: dotColor,
                      boxShadow: `0 0 5px ${dotColor}`,
                    }} />
                  </InlineStack>

                  {kpis.map(kpi => (
                    <BlockStack key={kpi.id} gap="100">
                      <InlineStack align="space-between">
                        <Text as="p" variant="bodySm" tone="subdued">
                          {KPI_SHORT[kpi.id] ?? kpi.name}
                        </Text>
                        <Text as="p" variant="bodySm" fontWeight="semibold"
                          tone={statusTone(kpi) === 'success' ? 'success' : statusTone(kpi) === 'critical' ? 'critical' : undefined}
                        >
                          {kpi.formattedValue}
                        </Text>
                      </InlineStack>
                      {kpi.achievementPct != null && (
                        <ProgressBar
                          progress={Math.min(kpi.achievementPct, 100)}
                          tone={progressTone(kpi)}
                          size="small"
                        />
                      )}
                      {kpi.target != null && (
                        <Text as="p" variant="bodySm" tone="subdued">
                          Target: {kpi.target}{kpi.unit}
                        </Text>
                      )}
                    </BlockStack>
                  ))}
                </BlockStack>
              </div>
            )
          })}
        </div>
      </BlockStack>
    </Card>
  )
}
